import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { getApiErrorMessage, getApiErrorStatus } from '@/services/api/client';
import { serviceProviderApi } from '@/services/api/serviceProviderApi';
import type { ServiceProviderProfile } from '@/types';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isServiceProviderProfile(value: unknown): value is ServiceProviderProfile {
  return (
    isRecord(value) &&
    typeof Reflect.get(value, '_id') === 'string' &&
    typeof Reflect.get(value, 'userId') === 'string'
  );
}

function extractServiceProviderProfile(value: unknown): ServiceProviderProfile | null {
  if (isServiceProviderProfile(value)) {
    return value;
  }

  if (!isRecord(value)) {
    return null;
  }

  const nestedData = Reflect.get(value, 'data');
  if (nestedData !== undefined) {
    return extractServiceProviderProfile(nestedData);
  }

  const nestedProfile = Reflect.get(value, 'profile');
  if (nestedProfile !== undefined) {
    return extractServiceProviderProfile(nestedProfile);
  }

  return null;
}

interface ServiceProviderState {
  profile: ServiceProviderProfile | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchProfile: () => Promise<void>;
  createProfile: (data: Partial<ServiceProviderProfile>) => Promise<ServiceProviderProfile>;
  updateProfile: (data: Partial<ServiceProviderProfile>) => Promise<ServiceProviderProfile | null>;
  clearError: () => void;
}

export const useServiceProviderStore = create<ServiceProviderState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      profile: null,
      isLoading: false,
      error: null,
      lastFetched: null,

      fetchProfile: async () => {
        const { lastFetched, isLoading } = get();

        // Skip if recently fetched or already loading
        if (isLoading) return;
        if (lastFetched && Date.now() - lastFetched < CACHE_DURATION) return;

        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.getMyProfile();
          const profileData = extractServiceProviderProfile(response.data);
          set((state) => {
            state.profile = profileData;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });
        } catch (error: unknown) {
          const status = getApiErrorStatus(error);
          if (status === 404) {
            // No profile — not an error state
            set((state) => {
              state.profile = null;
              state.isLoading = false;
              state.lastFetched = Date.now();
            });
          } else {
            set((state) => {
              state.isLoading = false;
              state.error = getApiErrorMessage(
                error,
                'Failed to fetch service provider profile',
              );
            });
          }
        }
      },

      createProfile: async (data: Partial<ServiceProviderProfile>) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.createProfile(data);
          const profile = extractServiceProviderProfile(response.data);
          if (!profile) {
            throw new Error('Failed to create service provider profile');
          }
          set((state) => {
            state.profile = profile;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });
          return profile;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getApiErrorMessage(
              error,
              'Failed to create service provider profile',
            );
          });
          throw error;
        }
      },

      updateProfile: async (data: Partial<ServiceProviderProfile>) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const response = await serviceProviderApi.updateMyProfile(data);
          const profile = extractServiceProviderProfile(response.data);
          if (!profile) {
            throw new Error('Failed to update service provider profile');
          }
          set((state) => {
            state.profile = profile;
            state.isLoading = false;
            state.lastFetched = Date.now();
          });
          return profile;
        } catch (error: unknown) {
          set((state) => {
            state.isLoading = false;
            state.error = getApiErrorMessage(
              error,
              'Failed to update service provider profile',
            );
          });
          throw error;
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
    }))
  )
);
